import React, { useCallback } from 'react';
import type { FC } from 'react';
import styled from 'styled-components';
import { QuizAnswerButton } from 'components';
import { mobile, tablet } from 'styles/breakpoints';
import Header from './Header';
import ContinueButton from './QuizContinueButton';
import { Question, QuestionOption } from 'types/quiz';
import { HEADER_HEIGHT, HEADER_HEIGHT_MOBILE } from 'components/Header';
import { useSelector } from 'react-redux';
import { AppState } from 'state/types';
import { Color } from 'utils/theme';

interface MultipleAnswerProps {
  question: Question;
  selectedOptions: QuestionOption[];
  disabled: boolean;
  onSelect: (option: QuestionOption, maxSelection?: number) => void;
  onContinue: () => void;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 3.5rem 0 7rem;
  gap: 1.25rem;
  max-width: 28.75rem;
  width: 100%;
  @media ${tablet} {
    padding: 1.5rem 1rem 6rem;
  }
  @media ${mobile} {
    padding: 1.5rem 1rem 6rem;
  }
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  width: 100%;
  @media ${tablet} {
    flex-direction: column;
    padding: 0;
  }
`;

const Option = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-self: stretch;
  button {
    width: 100%;
  }
`;

const MultipleByRelationshipStatus: FC<MultipleAnswerProps> = ({
  question,
  selectedOptions,
  disabled,
  onSelect,
  onContinue,
}) => {
  const {
    type,
    label,
    image,
    alt,
    optionsLabel,
    options,
    maxSelection,
    imageHeightDesktop,
    imageHeightTablet,
    imageHeightMobile,
  } = question;

  const quizAnswers = useSelector((state: AppState) => state.user.quiz_answers);
  const relationshipStatus = quizAnswers?.relationship_status;

  const filteredOptions = Object.keys(options)
    .filter(key => key.includes(relationshipStatus))
    .map(key => options[key]);

  const optionsToRender = filteredOptions.length > 0 ? filteredOptions[0] : [];

  const selected = useCallback(
    (option: QuestionOption) =>
      selectedOptions.some(
        selectedOption => selectedOption.value === option.value,
      ),
    [selectedOptions],
  );

  const renderOption = (option: QuestionOption, index: number) => {
    const maxSelected =
      !selected(option) &&
      Number(question.maxSelection) <= selectedOptions.length;

    return (
      <Option key={`${type}-answer-${index}`}>
        <QuizAnswerButton
          onClick={() => onSelect(option, Number(maxSelection))}
          active={selected(option)}
          maxSelected={maxSelected}
          emoji={option.emoji}
          isRound={true}
        >
          {option.label}
        </QuizAnswerButton>
      </Option>
    );
  };

  return (
    <Container>
      <Header
        title={label}
        subtitle={optionsLabel}
        imageUrl={image}
        imageAlt={alt}
        imageHeightDesktop={imageHeightDesktop}
        imageHeightTablet={imageHeightTablet}
        imageHeightMobile={imageHeightMobile}
      />
      <OptionsContainer>{optionsToRender?.map(renderOption)}</OptionsContainer>
      <ContinueButton
        color={question.color as Color}
        cornerVariant={question.cornerVariant}
        title={question?.nextBtnTitle}
        onClick={onContinue}
        disabled={disabled}
        hasBackground={true}
      />
    </Container>
  );
};

export default MultipleByRelationshipStatus;
